import React from 'react';
import { Card, CardContent, Typography, Chip, Box, Grid } from '@mui/material';
import { Campaign } from '../models/Campaign';
import { LocationOn } from '@mui/icons-material';

interface CampaignCardProps {
    campaign: Campaign;
    onClick: () => void;
}

// Detail row component
const DetailRow: React.FC<{ title: string; value: string }> = ({ title, value }) => {
    return (
        <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            py: 0.75
        }}>
            <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ fontWeight: 500 }}
            >
                {title}
            </Typography>
            <Typography 
                variant="body2"
                sx={{ 
                    color: 'text.primary',
                    fontWeight: 500
                }}
            >
                {value}
            </Typography>
        </Box>
    );
};

const CampaignCard: React.FC<CampaignCardProps> = ({ campaign, onClick }) => {
    return (
        <Card 
            sx={{ 
                mb: 2,
                cursor: 'pointer',
                overflow: 'visible',
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                }
            }}
            onClick={onClick}
        >
            <CardContent sx={{ p: 3 }}>
                {/* Header */}
                <Box 
                    display="flex" 
                    justifyContent="space-between" 
                    alignItems="flex-start" 
                    mb={3}
                >
                    <Box>
                        <Typography 
                            variant="h6" 
                            component="div" 
                            sx={{ 
                                mb: 1,
                                color: 'text.primary',
                                fontWeight: 700,
                                fontSize: '1.1rem',
                                lineHeight: 1.2
                            }}
                        >
                            {campaign.centre_name || 'Unnamed Center'}
                        </Typography>
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                color: 'text.secondary',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                fontSize: '0.9rem'
                            }}
                        >
                            <LocationOn sx={{ 
                                fontSize: 18,
                                color: 'primary.main'
                            }} />
                            {campaign.city || 'Unknown Location'}
                        </Typography>
                    </Box>
                    
                    {campaign.category && (
                        <Chip 
                            label={campaign.category} 
                            size="small" 
                            color="primary"
                            sx={{
                                fontWeight: 600,
                                backgroundColor: 'primary.50',
                                color: 'primary.700',
                                borderRadius: '6px',
                                height: '24px',
                                '& .MuiChip-label': {
                                    px: 1.5,
                                    fontSize: '0.75rem'
                                },
                            }}
                        />
                    )}
                </Box>
                
                {/* Details */}
                <Box 
                    sx={{
                        backgroundColor: 'grey.50',
                        borderRadius: 1.5,
                        p: 2
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <DetailRow title="Country" value={campaign.country || 'N/A'} />
                        <DetailRow title="Region" value={campaign.region || 'N/A'} />
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

export default CampaignCard; 